import { useMemo, useState } from "react";
import { Spinner } from "@/components/ui";
import { formatRelative } from "@/lib/format";
import { useAuth } from "@/staff/auth/AuthContext";
import type { ChatJob } from "@/types";

/** The left job list shared by Messages and Documents (§4.2): filter box, then
 *  rows in feed order (recency, then alphabetical no-conversation jobs).
 *  Snippets render only where the parent asks (Messages page). */
export function ChatSidebar({
  jobs,
  selectedJobId,
  onSelect,
  showSnippets = false,
}: {
  jobs: ChatJob[] | null;
  selectedJobId: number | null;
  onSelect: (jobId: number) => void;
  showSnippets?: boolean;
}) {
  const { user } = useAuth();
  const [filter, setFilter] = useState("");

  const list = useMemo(() => {
    if (!jobs) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter(
      (j) =>
        j.name.toLowerCase().includes(q) ||
        j.client_names.toLowerCase().includes(q) ||
        (j.builder_names ?? "").toLowerCase().includes(q) ||
        String(j.job_number).includes(q),
    );
  }, [jobs, filter]);

  return (
    <aside className="chat-side">
      <input
        type="search"
        className="chat-side__filter"
        placeholder="Filter jobs…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        aria-label="Filter jobs"
      />
      {jobs === null ? (
        <div className="chat-side__loading">
          <Spinner />
        </div>
      ) : list.length === 0 ? (
        <p className="chat-side__empty">
          {jobs.length === 0 ? "No portal-enabled jobs yet." : "No jobs match the filter."}
        </p>
      ) : (
        <ul className="chat-side__list">
          {list.map((j) => {
            const active = j.job_id === selectedJobId;
            return (
              <li key={j.job_id}>
                <button
                  type="button"
                  className={`chat-side__row${active ? " is-active" : ""}`}
                  onClick={() => onSelect(j.job_id)}
                  aria-current={active ? "true" : undefined}
                >
                  <span className="chat-side__row-top">
                    <span className="chat-side__job">
                      {j.job_number > 0 ? `${j.job_number} · ` : ""}
                      {j.name}
                    </span>
                    {j.unread_count > 0 ? (
                      <span className="chat-side__badge" aria-label={`${j.unread_count} unread`}>
                        {j.unread_count > 99 ? "99+" : j.unread_count}
                      </span>
                    ) : null}
                  </span>
                  <span className="chat-side__row-mid">
                    <span className="chat-side__client">
                      {j.client_names || "—"}
                      {j.builder_names ? <span className="chat-side__builders"> · {j.builder_names}</span> : null}
                    </span>
                    {j.last_message_at > 0 ? (
                      <span className="chat-side__time">{formatRelative(j.last_message_at)}</span>
                    ) : null}
                  </span>
                  {showSnippets && j.last_message_snippet ? (
                    <span className="chat-side__snippet">
                      {/* "You:" only when it really was you — a colleague's last
                          message gets their first name (D6). Legacy rows resolve
                          to no author name; the practice name is honest there. */}
                      {j.last_message_sender_type === "staff"
                        ? `${
                            j.last_message_sender_user_id === user?.id
                              ? "You"
                              : j.last_message_sender_name.split(" ")[0] ||
                                "Arkitectly"
                          }: `
                        : ""}
                      {j.last_message_snippet}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}